/**
 * TemplateSelector - Dropdown for selecting insole templates
 * 
 * Displays available templates with name, size, and type information.
 */

import React from 'react'
import type { Template } from '../../../services/types'
import './TemplateSelector.css'

interface TemplateSelectorProps {
  templates: Template[]
  selectedTemplate: Template | null
  onSelect: (template: Template) => void
  disabled?: boolean
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates,
  selectedTemplate,
  onSelect,
  disabled = false
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = e.target.value
    const template = templates.find(t => t.id === templateId)
    if (template) {
      onSelect(template)
    }
  }

  const formatTemplateLabel = (template: Template) => {
    const parts = [template.name]
    if (template.size) parts.push(`Size ${template.size}`)
    if (template.type) parts.push(template.type)
    return parts.join(' - ')
  }

  return (
    <div className="template-selector">
      <select
        value={selectedTemplate?.id || ''}
        onChange={handleChange}
        disabled={disabled || templates.length === 0}
        className="template-select"
      >
        <option value="" disabled>
          {templates.length === 0 ? 'No templates available' : 'Select a template...'}
        </option>
        {templates.map(template => (
          <option key={template.id} value={template.id}>
            {formatTemplateLabel(template)}
          </option>
        ))}
      </select>

      {selectedTemplate && (
        <div className="template-info">
          <div className="info-row">
            <span className="info-label">Name:</span>
            <span className="info-value">{selectedTemplate.name}</span>
          </div>
          {selectedTemplate.size && (
            <div className="info-row">
              <span className="info-label">Size:</span>
              <span className="info-value">{selectedTemplate.size}</span>
            </div>
          )}
          {selectedTemplate.type && (
            <div className="info-row">
              <span className="info-label">Type:</span>
              <span className="info-value">{selectedTemplate.type}</span>
            </div>
          )}
          {selectedTemplate.description && (
            <div className="info-row">
              <span className="info-label">Description:</span>
              <span className="info-value">{selectedTemplate.description}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
